import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Modal,
  message,
  Spin,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";

type Candidate = {
  id: number;
  firstName: string;
  lastName: string;
};

type AssetRequestPayload = {
  candidate_id: number;
  requested_asset_type: "Laptop" | "Monitor" | "Other";

  ship_to_name: string;
  ship_to_email?: string | null;
  ship_to_company?: string | null;

  shipping_address1: string;
  shipping_address2?: string | null;
  shipping_city: string;
  shipping_state: string;
  shipping_postal_code: string;
  shipping_country: string;

  requested_by_email?: string | null;
  needed_by_date?: string | null; // YYYY-MM-DD
};

const { TextArea } = Input;

const AssetRequestForm: React.FC = () => {
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successId, setSuccessId] = useState<number | null>(null);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidateLoading, setCandidateLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setCandidateLoading(true);
      try {
        const res = await axios.get("/active-placement-candidates");
        if (!mounted) return;

        const data = Array.isArray(res.data) ? res.data : [];
        setCandidates(data);
      } catch (err) {
        console.error(err);
        message.error("Failed to load candidates.");
      } finally {
        if (mounted) setCandidateLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const candidateOptions = useMemo(
    () =>
      candidates.map((c) => ({
        value: c.id,
        label: `${c.lastName}, ${c.firstName} (ID: ${c.id})`,
      })),
    [candidates],
  );

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();

      const payload: AssetRequestPayload = {
        candidate_id: Number(values.candidate_id),
        requested_asset_type: values.requested_asset_type,

        ship_to_name: values.ship_to_name,
        ship_to_email: values.ship_to_email?.trim()
          ? values.ship_to_email.trim()
          : null,
        ship_to_company: values.ship_to_company?.trim()
          ? values.ship_to_company.trim()
          : null,

        shipping_address1: values.shipping_address1,
        shipping_address2: values.shipping_address2?.trim()
          ? values.shipping_address2.trim()
          : null,
        shipping_city: values.shipping_city,
        shipping_state: values.shipping_state,
        shipping_postal_code: values.shipping_postal_code,
        shipping_country: values.shipping_country || "USA",

        requested_by_email: values.requested_by_email?.trim()
          ? values.requested_by_email.trim()
          : null,
        needed_by_date: values.needed_by_date
          ? dayjs(values.needed_by_date).format("YYYY-MM-DD")
          : null,
      };

      const res = await axios.post("/api/asset-requests", payload);

      const requestId = res?.data?.request_id ?? null;
      setSuccessId(requestId);
      setSuccessOpen(true);

      form.resetFields();
      form.setFieldsValue({
        requested_asset_type: "Laptop",
        shipping_country: "USA",
      });
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.response?.data?.error ||
          "Submit failed. Check required fields and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card style={{ maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 0 }}>Asset Request</h2>
      <p style={{ marginTop: 8, opacity: 0.8 }}>
        Request a laptop/monitor for a candidate.
      </p>

      <Form
        form={form}
        layout='vertical'
        initialValues={{
          requested_asset_type: "Laptop",
          shipping_country: "USA",
        }}
      >
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            label='Candidate'
            name='candidate_id'
            rules={[{ required: true, message: "Candidate is required." }]}
          >
            <Select
              showSearch
              placeholder={candidateLoading ? "Loading..." : "Select candidate"}
              loading={candidateLoading}
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              notFoundContent={candidateLoading ? <Spin size='small' /> : null}
              options={candidateOptions}
            />
          </Form.Item>

          <Form.Item
            label='Requested Asset Type'
            name='requested_asset_type'
            rules={[{ required: true, message: "Asset type is required." }]}
          >
            <Select
              options={[
                { value: "Laptop", label: "Laptop" },
                { value: "Monitor", label: "Monitor" },
                { value: "Other", label: "Other" },
              ]}
            />
          </Form.Item>
        </div>

        <h4 style={{ marginTop: 8 }}>Ship To</h4>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            label='Name'
            name='ship_to_name'
            rules={[{ required: true, message: "Ship to name is required." }]}
          >
            <Input placeholder='e.g., Jane Doe' />
          </Form.Item>

          <Form.Item
            label='Ship To Email (optional)'
            name='ship_to_email'
            rules={[{ type: "email", message: "Enter a valid email address." }]}
          >
            <Input placeholder='e.g., jane@example.com' />
          </Form.Item>

          <Form.Item label='Company (optional)' name='ship_to_company'>
            <Input placeholder='e.g., Acme Corp' />
          </Form.Item>
        </div>

        <h4 style={{ marginTop: 8 }}>Shipping Address</h4>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            label='Address Line 1'
            name='shipping_address1'
            rules={[{ required: true, message: "Address line 1 is required." }]}
          >
            <Input placeholder='123 Main St' />
          </Form.Item>

          <Form.Item label='Address Line 2 (optional)' name='shipping_address2'>
            <Input placeholder='Apt, suite, etc.' />
          </Form.Item>

          <Form.Item
            label='City'
            name='shipping_city'
            rules={[{ required: true, message: "City is required." }]}
          >
            <Input placeholder='Orlando' />
          </Form.Item>

          <Form.Item
            label='State'
            name='shipping_state'
            rules={[{ required: true, message: "State is required." }]}
          >
            <Input placeholder='FL' />
          </Form.Item>

          <Form.Item
            label='Postal Code'
            name='shipping_postal_code'
            rules={[{ required: true, message: "Postal code is required." }]}
          >
            <Input placeholder='32801' />
          </Form.Item>

          <Form.Item
            label='Country'
            name='shipping_country'
            rules={[{ required: true, message: "Country is required." }]}
          >
            <Input placeholder='USA' />
          </Form.Item>
        </div>

        <h4 style={{ marginTop: 8 }}>Request Details</h4>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            label='Requested By Email (optional)'
            name='requested_by_email'
            rules={[{ type: "email", message: "Enter a valid email address." }]}
          >
            <Input placeholder='requestor@company.com' />
          </Form.Item>

          <Form.Item label='Needed By Date (optional)' name='needed_by_date'>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item label='Notes (optional)' name='notes'>
          <TextArea
            rows={3}
            placeholder='Any details ops should know (timing, special instructions, etc.).'
          />
        </Form.Item>

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <Button type='primary' loading={submitting} onClick={onSubmit}>
            Submit Request
          </Button>
        </div>
      </Form>

      <Modal
        open={successOpen}
        onCancel={() => setSuccessOpen(false)}
        onOk={() => setSuccessOpen(false)}
        title='Request Submitted'
      >
        <p>
          Asset request saved successfully
          {successId ? ` (ID: ${successId})` : ""}.
        </p>
      </Modal>
    </Card>
  );
};

export default AssetRequestForm;
